import React, { useEffect } from 'react';
import EducatorQuestion from '../EducatorQuestion/EducatorQuestion';

/**
 * QuestionPreviewModal Component
 * Displays a question preview in a modal using the EducatorQuestion component
 */
const QuestionPreviewModal = ({ show, onHide, questionId, token }) => {
  if (!show) return null;

  /**
   * Handle modal close
   */
  const handleClose = () => {
    onHide();
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Call MathJax v3 to render LaTeX after modal is shown and content is loaded
   */
  useEffect(() => {
    if (!show || !questionId) {
      return;
    }

    // Function to trigger MathJax rendering for the modal content
    const renderMathJax = () => {
      const modalBody = document.getElementById('question-preview-modal-body');
      
      if (!modalBody) {
        return;
      }

      // Find all math elements in the modal - try multiple selectors
      const mathElements1 = modalBody.querySelectorAll('.mathformula');
      const mathElements2 = modalBody.querySelectorAll('[data-tex]');
      const mathElements3 = modalBody.querySelectorAll('span[class*="math"]');
      const allMathElements = [
        ...Array.from(mathElements1),
        ...Array.from(mathElements2),
        ...Array.from(mathElements3)
      ];
      
      if (typeof window.MathJax !== 'undefined' && window.MathJax.typesetPromise) {
        try {
          // Filter out elements that are no longer in the DOM
          const validElements = allMathElements.filter(el => el && document.contains(el));
          const validModalBody = modalBody && document.contains(modalBody) ? modalBody : null;
          
          // Only proceed if we have valid elements
          if (validElements.length > 0 || validModalBody) {
            const targetElements = validElements.length > 0 ? validElements : (validModalBody ? [validModalBody] : []);
            
            window.MathJax.typesetPromise(targetElements).then(() => {
              // Success
            }).catch((error) => {
              // Silently handle errors
            });
          }
        } catch (error) {
          // Silently handle errors
        }
      } else if (typeof window.typesetMathJax === 'function') {
        try {
          if (allMathElements.length > 0) {
            window.typesetMathJax(allMathElements);
          } else {
            window.typesetMathJax(modalBody);
          }
        } catch (error) {
          // Silently handle errors
        }
      } else if (typeof window.MathJax !== 'undefined' && window.MathJax.Hub) {
        try {
          window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
        } catch (error) {
          // Silently handle errors
        }
      }
    };

    // Wait for DOM to be ready, then start checking
    const initialDelay = setTimeout(() => {
      renderMathJax();
    }, 100);

    // Use multiple timeouts to catch content that loads asynchronously
    const timer1 = setTimeout(() => {
      renderMathJax();
    }, 500);
    
    const timer2 = setTimeout(() => {
      renderMathJax();
    }, 1000);
    
    const timer3 = setTimeout(() => {
      renderMathJax();
    }, 2000);
    
    const timer4 = setTimeout(() => {
      renderMathJax();
    }, 3000);

    // Also listen for content changes in the modal body
    const checkModalBody = setInterval(() => {
      const modalBody = document.getElementById('question-preview-modal-body');
      if (modalBody) {
        clearInterval(checkModalBody);
        
        const observer = new MutationObserver((mutations) => {
          const hasContentChanges = mutations.some(mutation => 
            mutation.addedNodes.length > 0 || 
            mutation.removedNodes.length > 0 ||
            (mutation.type === 'characterData' && mutation.target.textContent.trim().length > 0)
          );
          if (hasContentChanges) {
            setTimeout(renderMathJax, 100);
          }
        });
        
        observer.observe(modalBody, {
          childList: true,
          subtree: true,
          characterData: true
        });

        // Store observer for cleanup
        window._mathjaxObserver = observer;
      }
    }, 100);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearInterval(checkModalBody);
      if (window._mathjaxObserver) {
        window._mathjaxObserver.disconnect();
        delete window._mathjaxObserver;
      }
    };
  }, [show, questionId, token]);

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block', 
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1050,
        overflow: 'auto'
      }} 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="question-preview-title"
    >
      <div 
        className="modal-dialog modal-xl" 
        style={{ 
          maxWidth: '95%', 
          width: '95%',
          minHeight: '90%',
          margin: '2.5% auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="modal-content"
          style={{
            minHeight: '90%',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh'
          }}
        >
          {/* Modal Header */}
          <div className="modal-header" style={{ flexShrink: 0 }}>
            <h4 className="modal-title" id="question-preview-title">Question Preview</h4>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              aria-label="Close"
            />
          </div>

          {/* Modal Body */}
          <div 
            id="question-preview-modal-body"
            className="modal-body" 
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '1rem'
            }}
          >
            {questionId ? (
              <EducatorQuestion 
                key={`question-${questionId}`}
                questionId={questionId} 
                token={token}
                hideComments={true}
              />
            ) : (
              <div className="text-center text-muted">
                <p>Loading preview...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionPreviewModal;

